import { useTranslation } from "react-i18next";
import { IsLoading, useLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState } from "../../../common/state/slice/appStateSlice";
import ButtonSquareIconsProjectGroupEnduser from "../../../common/components/Button/ButtonSquareIconsProjectGroupEnduser";
import PlaceholderWithIcon from "../../../common/components/PlaceholderWithIcon/PlaceholderWithIcon";
import type { IProjectGroup } from "../../../model/model";
import type { ThreeDotsItemKind } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import type IProjectGroupsCompletionItem from "../../enduserStats/dataTypes/IProjectGroupsCompletionItem";
import type { IProjectGroupWithProjects } from "../state/projectGroupsSlice";
import { fetchSearchIndex } from "../state/projectGroupsSlice";
import ButtonSquareIconsProjectGroupCreator from "src/common/components/Button/ButtonSquareIconsProjectGroupCreator";
import { useEffect, useState } from "react";
import { useAppDispatch } from "src/common/state/hooks";
import EmptyState from "src/features/statsNew/components/EmptyState";

export const PROJECT_GROUP_LIST_COMPONENT_ID = "projectGroupListComponent";
export const PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID = "projectGroupListWithLimitComponent";
export const PROJECT_GROUP_LIST_PROJECTS = "projectGroupListProjects";

export interface IProjectGroupIdAndName {
  id: number;
  name: string;
}

type ProjectGroupListProps = {
  projectGroups?: IProjectGroup[] | IProjectGroupsCompletionItem[];
  projectGroupsWithNestedElements?: IProjectGroupWithProjects[];
  componentId?: string;
  simplified?: boolean;
  handleClick: (projectGroup: IProjectGroupWithProjects | IProjectGroupsCompletionItem) => void;
  handleThreeDotButtonClick: (e: {
    item: ThreeDotsItemKind;
    itemId: number;
    action: string;
  }) => void;
  isEnduser?: boolean;
  setEditedProjectGroupId?: (id: number, projectGroup: IProjectGroupWithProjects) => void;
  searchQuery?: string;
};

const ProjectGroupList = ({
  handleThreeDotButtonClick,
  handleClick,
  simplified,
  projectGroups = [],
  projectGroupsWithNestedElements = [],
  isEnduser = false,
  searchQuery = "",
}: ProjectGroupListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const pgsLoadingState = useLoading(PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID);
  const projectGroupsAreLoading = pgsLoadingState !== LoadingState.Success;

  const [appWidth, setAppWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setAppWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const numberOfProjectGroupToDisplayInSimplifiedMode = appWidth < 1280 ? 1 : 2;

  useEffect(() => {
    if (!simplified && !isEnduser) {
      void dispatch(fetchSearchIndex({}));
    }
  }, [simplified, isEnduser, dispatch]);

  const projectGroupsWithNestedElementsList = simplified
    ? [...projectGroupsWithNestedElements].slice(0, numberOfProjectGroupToDisplayInSimplifiedMode)
    : projectGroupsWithNestedElements;

  const projectGroupList = simplified
    ? projectGroups
        .slice(0, isEnduser ? 3 : numberOfProjectGroupToDisplayInSimplifiedMode)
        .sort((a, b) => {
          const aTs = "updated_at_ts" in a ? a.updated_at_ts : 0;
          const bTs = "updated_at_ts" in b ? b.updated_at_ts : 0;
          return bTs - aTs;
        })
    : projectGroups;

  // Subcomponent to render groups
  const ProjectGroups = () => {
    if (!projectGroupsAreLoading) {
      if (
        (isEnduser && projectGroupList.length === 0) ||
        (!isEnduser && projectGroupsWithNestedElementsList.length === 0)
      ) {
        const isSearching = searchQuery.trim().length > 0;

        if (isSearching) {
          return (
            <div className="w-full py-8">
              <EmptyState
                icon="search"
                title={t("statsNew.emptyState.noSearchResults")}
                description={t("statsNew.emptyState.noSearchResultsDescription")}
              />
            </div>
          );
        }

        return (
          <div
            className={`${
              isEnduser ? "h-40" : "absolute h-72 left-[40%] flex items-center justify-center"
            }`}
          >
            <PlaceholderWithIcon
              className={`flex flex-col items-center ${isEnduser ? "h-4/5" : "h-2/5"}`}
              title={t("pages.project.noProjectGroupTitle")}
              text={
                isEnduser
                  ? t("pages.project.noProjectGroupBodyEnduser")
                  : t("pages.project.noProjectGroupBody")
              }
              heroIcon={isEnduser ? "InformationCircleIcon" : "FolderAddIcon"}
            />
          </div>
        );
      }
    }

    if (isEnduser && projectGroupList.length <= 3 && projectGroupsAreLoading) {
      return (
        <IsLoading componentId={PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID} showSuccess={false} />
      );
    } else {
      return (
        <div className="w-full flex flex-col justify-start align-center">
          <IsLoading componentId={PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID} showSuccess={false}>
            <div
              className={`grid ${
                isEnduser
                  ? "xl:grid-cols-6 md:grid-cols-4 grid-cold:2"
                  : simplified
                  ? "grid-cols-2 xl:grid-cols-2 md:grid-cols-1"
                  : ""
              } gap-6 md:gap-10`}
            >
              {isEnduser
                ? projectGroupList.map(
                    (projectGroup: IProjectGroup | IProjectGroupsCompletionItem, i: number) => {
                      const enduserPg = projectGroup as IProjectGroupsCompletionItem;
                      return (
                        <ButtonSquareIconsProjectGroupEnduser
                          key={`zblu=${i * 33}`}
                          handleThreeDotButtonClick={handleThreeDotButtonClick}
                          currentProjectGroup={enduserPg.projectGroupId}
                          projectGroup={enduserPg}
                          onClick={() => handleClick(enduserPg)}
                          isEnduser={isEnduser}
                        />
                      );
                    },
                  )
                : projectGroupsWithNestedElementsList.map(
                    (pgWithNestedElementList: IProjectGroupWithProjects, i: number) => {
                      return (
                        <ButtonSquareIconsProjectGroupCreator
                          key={`zblu=${i * 22}`}
                          projectGroupWithProjects={pgWithNestedElementList}
                          handleThreeDotButtonClick={handleThreeDotButtonClick}
                          openProjectGroup={handleClick}
                          simplified={simplified}
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleClick(pgWithNestedElementList);
                          }}
                        />
                      );
                    },
                  )}
              {simplified &&
                projectGroupsAreLoading &&
                projectGroupsWithNestedElements.length === 0 && (
                  <IsLoading
                    componentId={PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID}
                    showSuccess={false}
                    spinnerPlaceholder
                  />
                )}
            </div>
          </IsLoading>
        </div>
      );
    }
  };

  return <ProjectGroups />;
};

export default ProjectGroupList;
